import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ISearchResult } from 'types/searchInterfaces'
import colors from 'constants/colors'
import { deriveMatchQuality, MatchQuality } from './matchQuality'

/** @notExported */
interface IFreeTextMatchInfoProps {
  /** The free text search result to explain. */
  result: ISearchResult
}

const CHUNK_TYPE_LABEL_KEYS: Record<string, string> = {
  PROFILE: 'search.chunkType.PROFILE',
  SKILLS: 'search.chunkType.SKILLS',
  QUALIFICATIONS: 'search.chunkType.QUALIFICATIONS',
  BACKGROUND: 'search.chunkType.BACKGROUND',
  EMPLOYMENT: 'search.chunkType.EMPLOYMENT',
  PROJECT: 'search.chunkType.PROJECT',
}

const QUALITY_LABEL_KEYS: Record<MatchQuality, string> = {
  strong: 'search.matchQuality.strong',
  moderate: 'search.matchQuality.moderate',
  weak: 'search.matchQuality.weak',
}

const QUALITY_CHIP_COLOR: Record<MatchQuality, 'success' | 'warning' | 'default'> = {
  strong: 'success',
  moderate: 'warning',
  weak: 'default',
}

/**
 * Quiet footnote explaining why a free text search result matched: a chip for which CV section
 * matched best, a colored chip for match quality/confidence, the AI's stated reason for that
 * quality (or a note that this result wasn't sent to the AI at all, e.g. reranking is disabled or
 * it ranked outside the reranked top-N), and a one-line excerpt of the matched text itself. Sits
 * below the main result content rather than above it, since it's supporting evidence, not the
 * headline.
 *
 * @param result - The free text search result to explain.
 * @returns The match explanation, or nothing if the result doesn't carry match data.
 * @notExported
 */
const FreeTextMatchInfo: React.FC<IFreeTextMatchInfoProps> = ({ result }) => {
  const { t } = useTranslation()

  if (!result.contentText || !result.chunkType) {
    return null
  }

  const quality = deriveMatchQuality(result)

  const sectionLabelKey = CHUNK_TYPE_LABEL_KEYS[result.chunkType]
  const sectionLabel = sectionLabelKey ? t(sectionLabelKey) : result.chunkType
  const aiReasonText = result.llmReason || t('search.aiRerankNotApplied')

  return (
    <Grid size={12} mt={1.5} pt={1} sx={{ borderTop: `1px solid ${colors.borderColor}` }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid>
          <Chip size="small" color={QUALITY_CHIP_COLOR[quality]} label={t(QUALITY_LABEL_KEYS[quality])} />
        </Grid>
        <Grid>
          <Chip size="small" variant="outlined" color="secondary" label={sectionLabel} />
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
        {aiReasonText}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          mt: 0.5,
        }}
      >
        {result.contentText}
      </Typography>
    </Grid>
  )
}

export default FreeTextMatchInfo
